import React, { useEffect, useState } from 'react';
import { db } from './firebase';
import './AdminDashboard.css';

/* ─────────────────────────────────────────────
   Status badge component
───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pending', cls: 'status-pending' },
    approved: { label: 'Approved', cls: 'status-approved' },
    rejected: { label: 'Rejected', cls: 'status-rejected' },
  };
  const { label, cls } = map[status] || map.pending;
  return <span className={`adm-status ${cls}`}>{label}</span>;
}

/* ─────────────────────────────────────────────
   Main AdminDashboard component
   Displays all agent registrations with:
   - Agent UID  (auto-generated, searchable)
   - Full Name
   - Agency Name
   - Phone (verified)
   - Email
   - Reference Code
   - NID docs, Face photo links
   - Submission timestamp
   - Status badge + approve/reject actions
───────────────────────────────────────────── */
function AdminDashboard() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReg, setSelectedReg] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      // Attempt Firestore fetch; falls back to mock data if not configured
      const snapshot = await db.collection('registrations').orderBy('timestamp', 'desc').get();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRegistrations(data);
    } catch (err) {
      // Use mock data for demonstration when Firebase isn't configured
      setRegistrations(MOCK_DATA);
    }
    setLoading(false);
  };

  /* Update status in Firestore */
  const handleStatusChange = async (id, newStatus) => {
    try {
      await db.collection('registrations').doc(id).update({ status: newStatus });
      setRegistrations(prev =>
        prev.map(r => r.id === id ? { ...r, status: newStatus } : r)
      );
      setSelectedReg(prev => prev && prev.id === id ? { ...prev, status: newStatus } : prev);
    } catch {
      // Update locally for demo
      setRegistrations(prev =>
        prev.map(r => r.id === id ? { ...r, status: newStatus } : r)
      );
    }
  };

  /* Filter + search */
  const filtered = registrations.filter(r => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      (r.agentUID || '').toLowerCase().includes(q) ||
      (r.agencyName || '').toLowerCase().includes(q) ||
      (r.fullName || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.phone || '').toLowerCase().includes(q);
    const matchesStatus = filterStatus === 'all' || (r.status || 'pending') === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const counts = {
    all: registrations.length,
    pending: registrations.filter(r => (r.status || 'pending') === 'pending').length,
    approved: registrations.filter(r => r.status === 'approved').length,
    rejected: registrations.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="adm-shell">

      {/* ── Sidebar ─────────────────────── */}
      <aside className="adm-side">
        <div className="adm-logo">
          <span className="material-symbols-outlined">location_on</span>
        </div>
        <span className="adm-logo-label">Lymet Admin</span>

        <nav className="adm-nav">
          <div className="adm-nav-item adm-nav-active">
            <span className="material-symbols-outlined">group</span>
            Agent Registrations
          </div>
          <div className="adm-nav-item">
            <span className="material-symbols-outlined">analytics</span>
            Analytics
          </div>
          <div className="adm-nav-item">
            <span className="material-symbols-outlined">settings</span>
            Settings
          </div>
        </nav>
      </aside>

      {/* ── Main content ─────────────────── */}
      <main className="adm-main">

        {/* Header */}
        <header className="adm-header">
          <div>
            <h1 className="adm-title">Agent Registrations</h1>
            <p className="adm-subtitle">Review and manage all agency applications</p>
          </div>
          <button className="adm-refresh-btn" onClick={fetchData}>
            <span className="material-symbols-outlined">refresh</span>
            Refresh
          </button>
        </header>

        {/* Stats row */}
        <div className="adm-stats-row">
          {[
            { key: 'all', label: 'Total', icon: 'group', color: '#5140b5' },
            { key: 'pending', label: 'Pending', icon: 'pending', color: '#d97706' },
            { key: 'approved', label: 'Approved', icon: 'check_circle', color: '#16a34a' },
            { key: 'rejected', label: 'Rejected', icon: 'cancel', color: '#dc2626' },
          ].map(({ key, label, icon, color }) => (
            <button
              key={key}
              className={`adm-stat-card ${filterStatus === key ? 'adm-stat-active' : ''}`}
              onClick={() => setFilterStatus(key)}
            >
              <span className="material-symbols-outlined adm-stat-icon" style={{ color }}>
                {icon}
              </span>
              <div>
                <div className="adm-stat-num" style={{ color }}>{counts[key]}</div>
                <div className="adm-stat-label">{label}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="adm-search-row">
          <div className="adm-search-wrap">
            <span className="material-symbols-outlined adm-search-icon">search</span>
            <input
              className="adm-search-input"
              type="text"
              placeholder="Search by Agent UID, name, email, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="adm-search-clear" onClick={() => setSearch('')}>
                <span className="material-symbols-outlined">close</span>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        {loading && (
          <div className="adm-loading">
            <div className="adm-spinner" />
            <span>Loading registrations…</span>
          </div>
        )}

        {!loading && (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Agent UID</th>
                  <th>Full Name</th>
                  <th>Agency</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Ref. Code</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="9" className="adm-empty">No registrations found.</td>
                  </tr>
                )}
                {filtered.map(reg => (
                  <tr key={reg.id} className="adm-table-row" onClick={() => setSelectedReg(reg)}>
                    <td>
                      <span className="adm-uid-chip">
                        {reg.agentUID || '—'}
                      </span>
                    </td>
                    <td className="adm-td-name">{reg.fullName || '—'}</td>
                    <td>{reg.agencyName || '—'}</td>
                    <td>
                      <div className="adm-phone-cell">
                        {reg.phone || '—'}
                        {reg.phone && (
                          <span className="material-symbols-outlined adm-verified-icon" title="Verified">
                            verified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="adm-td-email">{reg.email || '—'}</td>
                    <td>
                      {reg.referenceCode
                        ? <span className="adm-ref-chip">{reg.referenceCode}</span>
                        : <span className="adm-td-empty">—</span>
                      }
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <StatusBadge status={reg.status || 'pending'} />
                    </td>
                    <td className="adm-td-date">
                      {reg.timestamp
                        ? new Date(reg.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'
                      }
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="adm-action-btns">
                        <button
                          className="adm-btn-approve"
                          title="Approve"
                          disabled={(reg.status || 'pending') === 'approved'}
                          onClick={() => handleStatusChange(reg.id, 'approved')}
                        >
                          <span className="material-symbols-outlined">check</span>
                        </button>
                        <button
                          className="adm-btn-reject"
                          title="Reject"
                          disabled={reg.status === 'rejected'}
                          onClick={() => handleStatusChange(reg.id, 'rejected')}
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                        <button
                          className="adm-btn-view"
                          title="View Details"
                          onClick={() => setSelectedReg(reg)}
                        >
                          <span className="material-symbols-outlined">open_in_new</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* ── Detail Modal ───────────────── */}
      {selectedReg && (
        <div className="adm-modal-overlay" onClick={() => setSelectedReg(null)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div>
                <h2 className="adm-modal-title">Agent Details</h2>
                <span className="adm-uid-chip" style={{ fontSize: '0.8rem' }}>
                  {selectedReg.agentUID || 'N/A'}
                </span>
              </div>
              <button className="adm-modal-close" onClick={() => setSelectedReg(null)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="adm-modal-body">
              <div className="adm-detail-grid">
                {[
                  { icon: 'fingerprint', label: 'Agent UID', value: selectedReg.agentUID },
                  { icon: 'person', label: 'Full Name', value: selectedReg.fullName },
                  { icon: 'apartment', label: 'Agency Name', value: selectedReg.agencyName },
                  { icon: 'smartphone', label: 'Mobile', value: selectedReg.phone },
                  { icon: 'mail', label: 'Email', value: selectedReg.email },
                  { icon: 'confirmation_number', label: 'Ref. Code', value: selectedReg.referenceCode || '—' },
                  { icon: 'schedule', label: 'Submitted', value: selectedReg.timestamp ? new Date(selectedReg.timestamp).toLocaleString() : '—' },
                ].map(({ icon, label, value }) => (
                  <div key={label} className="adm-detail-row">
                    <span className="material-symbols-outlined adm-detail-icon">{icon}</span>
                    <div>
                      <div className="adm-detail-label">{label}</div>
                      <div className="adm-detail-value">{value || '—'}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Documents */}
              <div className="adm-docs-row">
                {selectedReg.nidUrl && (
                  <a href={selectedReg.nidUrl} target="_blank" rel="noopener noreferrer" className="adm-doc-link">
                    <span className="material-symbols-outlined">badge</span>
                    NID Document
                  </a>
                )}
                {selectedReg.faceUrl && (
                  <a href={selectedReg.faceUrl} target="_blank" rel="noopener noreferrer" className="adm-doc-link">
                    <span className="material-symbols-outlined">face</span>
                    Face Photo
                  </a>
                )}
              </div>

              {/* Status actions */}
              <div className="adm-modal-actions">
                <button
                  className="adm-modal-approve"
                  disabled={(selectedReg.status || 'pending') === 'approved'}
                  onClick={() => handleStatusChange(selectedReg.id, 'approved')}
                >
                  <span className="material-symbols-outlined">check_circle</span>
                  Approve
                </button>
                <button
                  className="adm-modal-reject"
                  disabled={selectedReg.status === 'rejected'}
                  onClick={() => handleStatusChange(selectedReg.id, 'rejected')}
                >
                  <span className="material-symbols-outlined">cancel</span>
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Mock data — used when Firebase isn't set up
───────────────────────────────────────────── */
const MOCK_DATA = [
  {
    id: 'mock1',
    agentUID: 'LMT-A3F9K2-20260306',
    fullName: 'Mohammad Rafiq Hossain',
    agencyName: 'Stellar Travel Agency',
    phone: '+8801712345678',
    email: 'rafiq@stellar.com',
    referenceCode: 'REF-2024-ALPHA',
    status: 'pending',
    timestamp: new Date().toISOString(),
  },
  {
    id: 'mock2',
    agentUID: 'LMT-BZ7M3X-20260306',
    fullName: 'Nadia Rahman',
    agencyName: 'Prime Tours BD',
    phone: '+8801987654321',
    email: 'nadia@primetours.com',
    referenceCode: '',
    status: 'approved',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: 'mock3',
    agentUID: 'LMT-CQ4N8Y-20260305',
    fullName: 'Karim Uddin',
    agencyName: 'Atlas Real Estate',
    phone: '+8801611223344',
    email: 'karim@atlasre.com',
    referenceCode: 'PARTNER-BD-001',
    status: 'rejected',
    timestamp: new Date(Date.now() - 172800000).toISOString(),
  },
];

export default AdminDashboard;
